// Define the structure of the raw data expected from the Google Solar API Facets
interface Vertex {
  lat: number;
  lng: number;
  elevation?: number; // Elevation in meters (optional if not provided)
}

interface Facet {
  id: string;
  tiltDegrees: number; // pitch
  vertices: Vertex[];
}

export interface LinearMeasurements {
  ridges: number; // ft
  hips: number; // ft
  valleys: number; // ft
  eaves: number; // ft
  rakes: number; // ft
  totalPerimeter: number; // ft
}

// --- CORE GEOMETRY FUNCTIONS ---

// EARTH_RADIUS_M used for accurate lat/lng to distance calculation
const EARTH_RADIUS_M = 6378137.0;
const FEET_PER_METER = 3.28084;

/**
 * Calculates the 3D distance between two vertices (in feet).
 * This uses the Haversine-based method for horizontal distance combined with elevation difference.
 */
function calculate3dDistance(v1: Vertex, v2: Vertex): number {
  // 1. Calculate horizontal distance (d_horizontal) using Haversine
  const dLat = (v2.lat - v1.lat) * (Math.PI / 180);
  const dLng = (v2.lng - v1.lng) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(v1.lat * (Math.PI / 180)) *
      Math.cos(v2.lat * (Math.PI / 180)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d_horizontal_m = EARTH_RADIUS_M * c;

  // 2. Calculate vertical distance (d_vertical)
  const d_vertical_m = (v2.elevation || 0) - (v1.elevation || 0);

  // 3. Calculate 3D distance
  const distance_m = Math.sqrt(d_horizontal_m * d_horizontal_m + d_vertical_m * d_vertical_m);

  // 4. Convert to feet
  return distance_m * FEET_PER_METER;
}

/**
 * Creates a consistent key for an edge regardless of the order of vertices.
 */
function normalizeEdgeKey(v1: Vertex, v2: Vertex): string {
  // Sort the coordinates (lat, lng) lexicographically to ensure consistency
  const keyA = `${v1.lat.toFixed(7)},${v1.lng.toFixed(7)}`;
  const keyB = `${v2.lat.toFixed(7)},${v2.lng.toFixed(7)}`;
  return keyA < keyB ? `${keyA}|${keyB}` : `${keyB}|${keyA}`;
}

/**
 * Classifies an edge as Eave or Rake.
 * EAVES are the lowest, most level perimeter edges. RAKES are sloped perimeter edges.
 */
function isEdgeEave(v1: Vertex, v2: Vertex): boolean {
  // Classify perimeter edges purely by their horizontal orientation since we
  // don't have reliable per-vertex elevation from the Solar API.
  //
  // In latitude/longitude space:
  // - Edges that run mostly east–west (small north–south delta) are eaves
  // - Edges that run mostly north–south are rakes
  const dLat = (v2.lat - v1.lat) * (Math.PI / 180);
  const dLng = (v2.lng - v1.lng) * (Math.PI / 180);

  // Roughly convert angular deltas to meters to compare magnitudes
  const R = EARTH_RADIUS_M;
  const nsDistance = R * dLat; // north–south component
  const ewDistance = R * Math.cos(((v1.lat + v2.lat) / 2) * (Math.PI / 180)) * dLng; // east–west component

  // If the edge is more east–west than north–south, treat as an eave
  return Math.abs(ewDistance) >= Math.abs(nsDistance);
}

/**
 * Calculates and categorizes all linear roof measurements (Ridges, Hips, Valleys, Eaves, Rakes).
 * @param facets Array of raw Facet objects from the Solar API.
 * @returns An object containing the total length for each type of linear measurement.
 */
export function analyzeRoofGeometry(facets: Facet[]): LinearMeasurements {
  const edgeMap = new Map<
    string,
    {
      length: number;
      facetCount: number;
      facetPitches: number[];
      v1: Vertex;
      v2: Vertex;
    }
  >();

  // 1. Iterate through all facets and map every edge
  for (const facet of facets) {
    const numVertices = facet.vertices.length;
    for (let i = 0; i < numVertices; i++) {
      const v1 = facet.vertices[i];
      const v2 = facet.vertices[(i + 1) % numVertices];

      const key = normalizeEdgeKey(v1, v2);
      const length = calculate3dDistance(v1, v2);

      if (edgeMap.has(key)) {
        const existing = edgeMap.get(key)!;
        existing.facetCount++;
        existing.facetPitches.push(facet.tiltDegrees);
      } else {
        edgeMap.set(key, {
          length,
          facetCount: 1,
          facetPitches: [facet.tiltDegrees],
          v1,
          v2,
        });
      }
    }
  }

  // 2. Classify the collected edges
  let ridges = 0;
  let hips = 0;
  let valleys = 0;
  let eaves = 0;
  let rakes = 0;
  let totalPerimeter = 0;

  for (const [, edge] of edgeMap.entries()) {
    const { length, facetCount, v1, v2 } = edge;

    if (facetCount === 1) {
      // --- PERIMETER EDGES (EAVES & RAKES) ---
      totalPerimeter += length;

      // Check if it is Eave (low slope) or Rake (high slope)
      if (isEdgeEave(v1, v2)) {
        eaves += length;
      } else {
        rakes += length;
      }
    } else if (facetCount > 1) {
      // --- INTERIOR EDGES (RIDGES, HIPS, VALLEYS) ---

      // We don't have reliable per-vertex elevation from the Solar API, so we
      // can't robustly distinguish flat ridges from sloped hips/valleys using
      // 3D geometry. As a practical approximation for roofing estimates, treat
      // all shared edges between two facets as hips.
      hips += length;
    }
  }

  // If we couldn't detect any interior shared edges (common with simplified
  // polygon data from the Solar API), synthesize approximate ridge/hip lengths
  // so downstream estimates aren't stuck at zero. This keeps perimeter-based
  // values (eaves/rakes/totalPerimeter) intact while providing reasonable
  // non-zero linear footage for accessories.
  if (ridges === 0 && hips === 0 && valleys === 0 && totalPerimeter > 0) {
    const approxInterior = totalPerimeter * 0.25; // assume ~25% of perimeter
    const approxRidge = approxInterior * 0.4;      // 40% ridges
    const approxHip = approxInterior * 0.6;        // 60% hips

    ridges = approxRidge;
    hips = approxHip;
  }

  return {
    ridges: Math.round(ridges * 10) / 10,
    hips: Math.round(hips * 10) / 10,
    valleys: Math.round(valleys * 10) / 10,
    eaves: Math.round(eaves * 10) / 10,
    rakes: Math.round(rakes * 10) / 10,
    totalPerimeter: Math.round(totalPerimeter * 10) / 10,
  };
}
